import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { Box, Button, Step, StepContent, StepLabel, Stepper, Typography } from '@mui/material';
import { cssGlobals } from '../../app';

const steps = [
  {
    label: 'Upload a local JSON-LD/CBOR-LD document',
    description: `This step is mandatory to start using the application. You should upload or drag & drop a local valid JSON-LD/CBOR-LD file to be able to make the conversion (encoding/decoding). The application displays on the bottom the size in Bytes of the document.`,
  },
  {
    label: 'Convert to CBOR-LD/JSON-LD',
    description: 'In this step, you need to click on the “converter” button on the bottom right side of the JSON-LD/CBOR-LD panel. This action validates the document and encodes it according to the CBOR-LD encoder/decoder algorithm. The result is shown in the right hand panel subdivided in 2 sub-panels (for the encoder function): the upper panel contains a more readable binary visualization of the result, while the bottom panel displays the hexadecimal encoding. The application also displays the size and ratio of the encoding function in Bytes.',
  },
  {
    label: 'Edit JSON-LD',
    description: `In this step, you can edit the file previously updated and convert it. Note that the CBOR-LD panel is read-only and can not be modified directly in the panel. Once you have finished with the edition, you need to click to convert to check the result.`,
  },
  {
    label: 'Download the CBOR-LD/JSON-LD document',
    description: `Once happy with the previous steps, you can decide to download the files for further exploitation using your favorite text editor or program. `,
  },
];

export interface StepsHandle {
  setActiveStep: (step: number) => void;
}

export const Steps = forwardRef<StepsHandle>((s, ref) => {
  const [activeStep, setActiveStep] = useState(0);

  useImperativeHandle(ref, () => ({
    setActiveStep: (step: number) => setActiveStep(step)
  }));

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  return (
    <Box sx={{ width: cssGlobals.containerWidth, mx: 'auto' }}>
      <Typography sx={{
        fontWeight: '500',
        fontSize: 'h6.fontSize',
        mb: '17px'
      }}>
        How to use:
      </Typography>
      <Stepper activeStep={activeStep} orientation="vertical">
        {steps.map((step, index) => (
          <Step key={step.label}>
            <StepLabel
              sx={{ cursor: 'pointer' }}
              onClick={() => index === activeStep
                ? setActiveStep(-1)
                : setActiveStep(index)
              }
              optional={
                index === (steps.length - 1) ? (
                  <Typography variant="caption">Last step</Typography>
                ) : null
              }
            >
              {step.label}
            </StepLabel>
            <StepContent>
              <Typography>{step.description}</Typography>
              <Box sx={{ mb: 2 }}>
                <div>
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    sx={{ mt: 1, mr: 1 }}
                  >
                    {index === steps.length - 1 ? 'Finish' : 'Continue'}
                  </Button>
                  <Button
                    disabled={index === 0}
                    onClick={handleBack}
                    sx={{ mt: 1, mr: 1 }}
                  >
                    Back
                  </Button>
                </div>
              </Box>
            </StepContent>
          </Step>
        ))}
      </Stepper>
    </Box>
  )
});
